import { useState, useEffect } from "react";
import AdminSidebar from "../../../sidebar/admin_sidebar";
import AdminHeader from "../../../header/admin_header";
import { NavLink, useLocation } from "react-router-dom";
import { Table, Modal, Button, Form } from "react-bootstrap";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config.json";

const NetZero = () => {
  const location = useLocation();
  const [parameters, setParameters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");

  const getParameters = async () => {
    setLoadError("");
    try {
      const { isSuccess, data } = await apiCall(
        `${config.ADMIN_API_URL}netzero/parameters`,
        {},
        {},
        "GET"
      );
      if (isSuccess && data) {
        setParameters(data || []);
      } else {
        setLoadError("Couldn't load parameters. Please try again.");
      }
    } catch (e) {
      console.error("Error fetching net-zero parameters:", e);
      setLoadError("Couldn't load parameters. Please try again.");
    }
  };

  useEffect(() => {
    getParameters();
  }, []);

  /** Latest (current) row per param_key, for the main table. */
  const currentByKey = Object.values(
    parameters.reduce((acc, p) => {
      const prev = acc[p.param_key];
      if (!prev || new Date(p.effective_from) > new Date(prev.effective_from)) {
        acc[p.param_key] = p;
      }
      return acc;
    }, {})
  );

  const filtered = currentByKey.filter((p) => {
    const s = searchTerm.toLowerCase();
    return (
      p.name?.toLowerCase().includes(s) ||
      p.param_key?.toLowerCase().includes(s)
    );
  });

  const handleEdit = (param) => {
    setEditing(param);
    setEditValue(param.value != null ? parseFloat(param.value) : "");
    setError("");
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (editValue === "" || isNaN(Number(editValue))) {
      setError("Please enter a valid number.");
      return;
    }
    try {
      const { isSuccess } = await apiCall(
        `${config.ADMIN_API_URL}netzero/parameters/${encodeURIComponent(editing.param_key)}`,
        {},
        { value: Number(editValue) },
        "PATCH"
      );
      if (isSuccess) {
        setShowEditModal(false);
        await getParameters();
      } else {
        setError("Failed to save. Please try again.");
      }
    } catch (e) {
      console.error("Error saving net-zero parameter:", e);
      setError("Failed to save. Please try again.");
    }
  };

  /** DECIMAL values arrive as strings ("0.042000"); parse to drop trailing zeros for display. */
  const formatNumber = (v) => (v === null || v === undefined ? "" : parseFloat(v));

  const renderValue = (p) => {
    if (p.type === "NUMBER") return formatNumber(p.value);
    return <span style={{ color: "#666", fontStyle: "italic" }}>{p.formula_text}</span>;
  };

  return (
    <div>
      <AdminSidebar dataFromParent={location.pathname} />
      <AdminHeader />
      <div className="main_wrapper">
        <div className="inner_wraapper">
          <div className="steps-form">
            <div className="steps-row setup-panel">
              <div className="tabs-top setting_admin my-0">
                <ul>
                  <li><NavLink to="/settings"> My Profile </NavLink></li>
                  <li><NavLink to="/settings/billing"> Billing </NavLink></li>
                  <li><NavLink to="/settings/access_management"> Access </NavLink></li>
                  <li><NavLink to="/settings/emission-category">Emission Category</NavLink></li>
                  <li><NavLink to="/settings/financial_year"> Financial Year </NavLink></li>
                  <li><NavLink to="/settings/sub-users">Sub Users</NavLink></li>
                  <li><NavLink to="/settings/category">Category</NavLink></li>
                  <li><NavLink to="/settings/carbon_emission"> Carbon Emission </NavLink></li>
                  <li><NavLink to="/settings/unit"> Unit </NavLink></li>
                  <li><NavLink to="/settings/netzero" className="activee"> Net Zero </NavLink></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="color_div_on framwork_2 pt-0">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h5 className="mb-0">SBTi Net-Zero Parameters (V1.3.1)</h5>
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ maxWidth: "400px" }}
              />
            </div>

            {loadError && <p style={{ color: "red" }}>{loadError}</p>}

            <div className="business_detail">
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Parameter</th>
                      <th>Type</th>
                      <th>Value / Formula</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p, index) => (
                      <tr key={p.id}>
                        <td>{index + 1}</td>
                        <td>{p.name}</td>
                        <td>{p.type}</td>
                        <td>{renderValue(p)}</td>
                        <td>
                          {p.type === "NUMBER" ? (
                            <Button variant="link" onClick={() => handleEdit(p)}>Edit</Button>
                          ) : (
                            <span style={{ color: "#999" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update value — {editing?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: "#666", fontSize: "0.9rem" }}>
            Current value: <b>{formatNumber(editing?.value)}</b>. Saving applies the new value
            from today — the old value stays on record for earlier periods.
          </p>
          <Form.Group className="mb-3">
            <Form.Label>New value</Form.Label>
            <Form.Control
              type="number"
              step="any"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
            />
          </Form.Group>
          {error && <p style={{ color: "red" }}>{error}</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveEdit}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NetZero;
